import { useState, useEffect, useCallback, useRef } from 'react';
import { kasaBankService } from '../services/kasaBankService';

const DEFAULT_LIMIT = 50;

/**
 * Custom hook encapsulating all data-loading logic for the Kasa & Bank page.
 *
 * Static data (saldo, daily/monthly stats, payments-by-type) lade sie raz
 * na lokalizacje. Listy: "Wszystkie operacje", "KP", "KW" sa paginowane
 * po stronie serwera i zaleza od swoich query objektow (limit, offset,
 * dateFrom, dateTo, ...).
 *
 * @param {Object|null} selectedLocation Current location from context
 * @param {Object} operacjeQuery         { limit, offset, dateFrom, dateTo, typPlatnosci }
 * @param {Object} kpQuery               { limit, offset, dateFrom, dateTo }
 * @param {Object} kwQuery               { limit, offset, dateFrom, dateTo }
 */
export function useKasaBankData(selectedLocation, operacjeQuery = {}, kpQuery = {}, kwQuery = {}) {
  const [saldo, setSaldo] = useState(null);

  const [operacje, setOperacje] = useState([]);
  const [operacjeTotal, setOperacjeTotal] = useState(0);
  const [operacjeLoading, setOperacjeLoading] = useState(false);

  const [dailySummary, setDailySummary] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [paymentsByType, setPaymentsByType] = useState(null);

  const [kpDocuments, setKpDocuments] = useState([]);
  const [kpDocumentsTotal, setKpDocumentsTotal] = useState(0);
  const [kpDocumentsLoading, setKpDocumentsLoading] = useState(false);

  const [kwDocuments, setKwDocuments] = useState([]);
  const [kwDocumentsTotal, setKwDocumentsTotal] = useState(0);
  const [kwDocumentsLoading, setKwDocumentsLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    limit = DEFAULT_LIMIT,
    offset = 0,
    dateFrom = '',
    dateTo = '',
    typPlatnosci = ''
  } = operacjeQuery || {};

  const {
    limit: kpLimit = DEFAULT_LIMIT,
    offset: kpOffset = 0,
    dateFrom: kpDateFrom = '',
    dateTo: kpDateTo = ''
  } = kpQuery || {};

  const {
    limit: kwLimit = DEFAULT_LIMIT,
    offset: kwOffset = 0,
    dateFrom: kwDateFrom = '',
    dateTo: kwDateTo = ''
  } = kwQuery || {};

  // Loader dla danych "statycznych" - saldo, podsumowania, payments-by-type
  const loadStaticData = useCallback(async (signal) => {
    if (!selectedLocation) return;

    setLoading(true);
    try {
      const currentLocationId = selectedLocation.id || selectedLocation.location_id;

      const [saldoRes, dailyRes, monthlyRes, paymentsRes] = await Promise.all([
        kasaBankService.getSaldo(currentLocationId, signal),
        kasaBankService.getDailySummary(null, currentLocationId, signal),
        kasaBankService.getMonthlyStats(null, null, currentLocationId, signal),
        kasaBankService.getPaymentsByType(null, null, currentLocationId, signal)
      ]);

      if (saldoRes.success) setSaldo(saldoRes.data);
      if (paymentsRes.success) setPaymentsByType(paymentsRes.data || {});

      if (dailyRes.success) {
        setDailySummary({
          total_in: dailyRes.data.total_income || 0,
          total_out: dailyRes.data.total_expense || 0,
          balance: (dailyRes.data.total_income || 0) - (dailyRes.data.total_expense || 0)
        });
      }

      if (monthlyRes.success) {
        const totalTransactions = monthlyRes.data.total_transactions || 0;
        const totalIncome = monthlyRes.data.total_income || 0;
        setMonthlyStats({
          total_transactions: totalTransactions,
          total_amount: totalIncome,
          avg_transaction: totalTransactions > 0 ? totalIncome / totalTransactions : 0
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      console.error('Blad ladowania danych statycznych:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  const loadOperacje = useCallback(async (signal) => {
    if (!selectedLocation) return;

    setOperacjeLoading(true);
    try {
      const currentLocationId = selectedLocation.id || selectedLocation.location_id;
      const res = await kasaBankService.getOperacje({
        limit, offset,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        typPlatnosci: typPlatnosci || undefined,
        locationId: currentLocationId
      }, signal);

      if (res?.success) {
        setOperacje(res.data?.operacje || []);
        setOperacjeTotal(typeof res.data?.total === 'number' ? res.data.total : (res.data?.operacje?.length || 0));
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      console.error('Blad ladowania operacji:', err);
      setError(err.message);
    } finally {
      setOperacjeLoading(false);
    }
  }, [selectedLocation, limit, offset, dateFrom, dateTo, typPlatnosci]);

  // Helper dla KP i KW (rozpoznaje obie sygnatury response - stara `data: [...]`
  // jako fallback i nowa `data: {documents, total, ...}`).
  const _readDocsResponse = (res) => {
    if (!res?.success) return { documents: [], total: 0 };
    const d = res.data;
    if (Array.isArray(d)) return { documents: d, total: d.length };
    return {
      documents: d?.documents || [],
      total: typeof d?.total === 'number' ? d.total : (d?.documents?.length || 0)
    };
  };

  const loadKpDocuments = useCallback(async (signal) => {
    if (!selectedLocation) return;
    setKpDocumentsLoading(true);
    try {
      const currentLocationId = selectedLocation.id || selectedLocation.location_id;
      const res = await kasaBankService.getKPDocuments({
        limit: kpLimit, offset: kpOffset,
        dateFrom: kpDateFrom || undefined,
        dateTo: kpDateTo || undefined,
        locationId: currentLocationId
      }, signal);
      const { documents, total } = _readDocsResponse(res);
      setKpDocuments(documents);
      setKpDocumentsTotal(total);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      console.error('Blad ladowania KP:', err);
      setError(err.message);
    } finally {
      setKpDocumentsLoading(false);
    }
  }, [selectedLocation, kpLimit, kpOffset, kpDateFrom, kpDateTo]);

  const loadKwDocuments = useCallback(async (signal) => {
    if (!selectedLocation) return;
    setKwDocumentsLoading(true);
    try {
      const currentLocationId = selectedLocation.id || selectedLocation.location_id;
      const res = await kasaBankService.getKWDocuments({
        limit: kwLimit, offset: kwOffset,
        dateFrom: kwDateFrom || undefined,
        dateTo: kwDateTo || undefined,
        locationId: currentLocationId
      }, signal);
      const { documents, total } = _readDocsResponse(res);
      setKwDocuments(documents);
      setKwDocumentsTotal(total);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      console.error('Blad ladowania KW:', err);
      setError(err.message);
    } finally {
      setKwDocumentsLoading(false);
    }
  }, [selectedLocation, kwLimit, kwOffset, kwDateFrom, kwDateTo]);

  // Static data - tylko zmiana lokalizacji
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    if (selectedLocation) {
      loadStaticData(controller.signal).then(() => { if (!isMounted) return; });
    }
    return () => { isMounted = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, loadStaticData]);

  // Operacje - refetch przy zmianie filtrow/paginacji
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    if (selectedLocation) {
      loadOperacje(controller.signal).then(() => { if (!isMounted) return; });
    }
    return () => { isMounted = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, limit, offset, dateFrom, dateTo, typPlatnosci, loadOperacje]);

  // KP - refetch przy zmianie filtrow/paginacji
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    if (selectedLocation) {
      loadKpDocuments(controller.signal).then(() => { if (!isMounted) return; });
    }
    return () => { isMounted = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, kpLimit, kpOffset, kpDateFrom, kpDateTo, loadKpDocuments]);

  // KW - refetch przy zmianie filtrow/paginacji
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    if (selectedLocation) {
      loadKwDocuments(controller.signal).then(() => { if (!isMounted) return; });
    }
    return () => { isMounted = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, kwLimit, kwOffset, kwDateFrom, kwDateTo, loadKwDocuments]);

  // Helper publiczny - kompletny reload (po dodaniu/edycji operacji)
  const reloadAllRef = useRef(null);
  reloadAllRef.current = () => {
    const controller = new AbortController();
    loadStaticData(controller.signal);
    loadOperacje(controller.signal);
    loadKpDocuments(controller.signal);
    loadKwDocuments(controller.signal);
  };
  const loadData = useCallback(() => {
    reloadAllRef.current?.();
  }, []);

  return {
    saldo,
    operacje,
    operacjeTotal,
    operacjeLoading,
    dailySummary,
    monthlyStats,
    paymentsByType,
    kpDocuments,
    kpDocumentsTotal,
    kpDocumentsLoading,
    kwDocuments,
    kwDocumentsTotal,
    kwDocumentsLoading,
    loading,
    error,
    setError,
    loadData
  };
}
